import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import {
  Form, Spin, Table,
  Input, Row, Col,
  Button, DatePicker, Select, notification,
  Modal, Divider,
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import FilterRemoveIcon from 'mdi-react/FilterRemoveIcon';
import FilterIcon from 'mdi-react/FilterIcon';
import PlusIcon from 'mdi-react/PlusIcon';
import {
  get, put, patch, isSuperAgentError,
  RequestWrapper, ResponseWrapper,
} from '../../../dataSource/requests';
import { backendResponseCodes } from '../../../utils/responseMessages';
import CustomPagination from '../../sharedComponents/customPagination/CustomPagination';
import { getUserPermissions } from '../../../utils/users';
import './Notifications.scss';

const Notifications = (): React.ReactElement => {
  interface CategoryFromListAll {
    active?: boolean;
    id?: number;
    name?: string;
  }

  interface NotificationFromListAll {
    active?: boolean;
    body?: string;
    creationDate?: string;
    desiredDateNotification?: string;
    id: number;
    notificationCategory?: CategoryFromListAll;
    title?: string;
    expirationDate?: string;
    messageSend?: boolean;
  }

  interface RelevantPaginationInfo {
    maxSize: number;
    numberOfPages: number;
    pageNumber: number;
    totalElements: number;
  }

  const navigate = useNavigate();
  const userPermissions = getUserPermissions();
  const [formFilterInstance] = Form.useForm();
  const [maxPeriodOfDaysFilter, setMaxPeriodOfDaysFilter] = useState<number>(30);
  const [loading, setLoading] = useState<boolean>(true);
  const [listCategories, setListCategories] = useState<Array<CategoryFromListAll>>([]);
  const [data, setData] = useState<Array<NotificationFromListAll>>([]);
  const [paginationInfo, setPaginationInfo] = useState<RelevantPaginationInfo>({
    maxSize: 15,
    numberOfPages: 1,
    pageNumber: 1,
    totalElements: 1,
  });

  const runOnMount = async (): Promise<void> => {
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    const requestReference: RequestWrapper = get({
      url: '/service/getMaxPeriodOfDaysFilter',

      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      response = await requestReference;
    } catch (error) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (response?.body?.code === 5012):
        // "Max period of days filter got successfully", 5012
        setMaxPeriodOfDaysFilter(response?.body?.maxPeriodOfDaysFilter);
        break;

      case (responseError?.body?.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        break;

      case (responseError?.body?.code === -4003):
        // "You do not have permission to perform this operation", -4003
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      case (responseError?.body?.code === -4000):
      default:
        // "Something unexpected happened", -4000
        notification.error({
          message: 'Erro',
          description: 'Erro ao carregar o período máximo de dias',
        });
        break;
    }
  };

  /**
   * @name validateDateRange
   * @param rule
   * @param value
   * @returns retorna a validação de data para o filtro de consulta
   */
  const validateDateRange = (_rule: any, value: Array<object>): Promise<void> => {
    if (
      moment(value[1])
        .subtract(maxPeriodOfDaysFilter - 1, 'day').startOf('day')
      >= moment(value[0]).endOf('day')
    ) {
      return Promise.reject(new Error(`A busca é limitada a um intervalo de ${maxPeriodOfDaysFilter} dias`));
    }

    return Promise.resolve();
  };

  /**
   * @name getFilterParams
   * @description captura as informações do formulário de filtragem de notificações
   * @returns retorna um objeto do formulario de filtragem para ser enviado como parametro
   * na listagem de notificações
   */
  const getFilterParams = useCallback(() => {
    const values = formFilterInstance.getFieldsValue();
    const params: {
      notificationCategoryId?: number | null,
      title?: string | null,
      active?: boolean,
      periodStart?: string,
      periodEnd?: string,
    } = {
      notificationCategoryId: null,
      title: null,
      active: true,
    };

    params.notificationCategoryId = values.categoryFilter || null;
    params.title = values.titleFilter || null;

    if (values.intervalFilter != null) {
      params.periodStart = moment(values.intervalFilter[0])
        .startOf('day')
        .format('YYYY-MM-DDTHH:mm:ss.SSSZ');

      params.periodEnd = moment(values.intervalFilter[1])
        .endOf('day')
        .format('YYYY-MM-DDTHH:mm:ss.SSSZ');
    }

    return params;
  }, [formFilterInstance]);

  /**
   * @name fetchData
   * @param {int} page
   * @description gerencia o tratamento de filtragem e paginação para o retorno da lista
   * @returns retorna a lsitagem das notificações filtradas e paginada
   */
  const fetchData = async (page: number): Promise<void> => {
    const params = getFilterParams();

    setLoading(true);
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    const requestReference: RequestWrapper = patch({
      url: '/notification/listAllNotification',
      params,

      query: {
        orderby: 'id',
        pageSize: 15,
        page: (page >= 1) ? page - 1 : 0,
        sort: 'DESC',
      },

      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      response = await requestReference;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (response?.body?.code === 7008):
        // "All notification listed successfully", 7008
        setData(response?.body.listAllNotificationOutput);
        setPaginationInfo((previous) => {
          return ({
            ...previous,
            ...response?.body.paginationInfo,
          });
        });

        break;

      case (responseError?.body?.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        break;

      case (responseError?.body?.code === -4003):
        // "You do not have permission to perform this operation", -4003
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.General.Forbidden.description.pt_br,
        });
        break;

      case (responseError?.body?.code === -5013):
        // "Invalid list service requests filters", -5013
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.Service
            .InvalidListServiceRequestsFilters.description.pt_br,
        });
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      default:
        notification.error({
          message: 'Erro',
          description: 'Ocorreu um erro ao buscar a listagem de notificações, tente novamente mais tarde.',
        });
        break;
    }
    setLoading(false);
  };

  /**
   * @name handleDeleteNotification
   * @param {int} id
   * @description exclusão da notificação no banco de dados
   * @returns retorna a exclusão da notificação no banco de dados
   */
  const handleDeleteNotification = async (id: number): Promise<void> => {
    let eventResponse: ResponseWrapper | undefined;
    let eventResponseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    try {
      setLoading(true);

      const targetNotification = data.find((i) => {
        return i.id === id;
      });

      if (!targetNotification) {
        return;
      }

      const params = {
        id,
        active: false,
        notificationCategoryId: targetNotification?.notificationCategory?.id,
        title: targetNotification.title,
        body: targetNotification.body,
        desiredDateNotification: targetNotification.desiredDateNotification,
        expirationDate: targetNotification.expirationDate,
      };

      const requestReference: RequestWrapper = put({
        url: '/notification/editNotification',
        params,

        frontConfigs: {
          navigateFunction: navigate,
        },
      });

      eventResponse = await requestReference;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        eventResponseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (eventResponse?.body?.code === 7007):
        // Notification edited successfully
        notification.success({
          message: 'Sucesso',
          description: 'Notificação removida.',
        });
        fetchData(1);
        break;

      case (eventResponseError?.body?.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        break;

      case (eventResponseError?.body?.code === -4003):
        // "You do not have permission to perform this operation", -4003
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.General.Forbidden.description.pt_br,
        });
        break;

      case (eventResponseError?.body?.code === -7001):
        // "Notification not found", -7001
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.Notification
            .NotificationNotFound.description.pt_br,
        });
        break;

      case (eventResponseError?.body?.code === -20000):
        // "Notification category not found", -20000
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.NotificationCategory
            .NotificationCategoryNotFound.description.pt_br,
        });
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      default:
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro ao finalizar a requisição. Por favor, tente novamente mais tarde.',
        });
        break;
    }

    setLoading(false);
  };

  /**
   * @name handleFilter
   * @description realiza a chamada de litagem de notificações filtradas e paginada
   * @returns retorna a listagem de notificações filtrada e paginada
   */
  const handleFilter = (): void => {
    fetchData(1);
  };

  /**
   * @name cleanFilterFields
   * @description gerencia a limpeza do formulário de filtragem
   */
  const cleanFilterFields = (): void => {
    formFilterInstance.setFieldsValue({
      categoryFilter: undefined,
      intervalFilter: [moment().subtract(30, 'day'), moment()],
      titleFilter: '',
    });

    fetchData(1);
  };

  /**
   * @name getDataCategories
   * @description carregar a lista de categorias disponiveis para ser utilizadas nas notificações
   * @returns retorna uma lista populada em dropdown para cadastro de notificação
   */
  const getDataCategories = async (): Promise<void> => {
    let categoriesResponse: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    const requestReference: RequestWrapper = get({
      url: '/notificationCategory/listAll',

      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      categoriesResponse = await requestReference;
    } catch (error: unknown) {
      console.log('error load categories', error);
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (categoriesResponse?.body?.code === 18002):
        // "Notification category listed successfully", 18002
        setListCategories(categoriesResponse?.body.listAllNotificationCategoryOutput);
        break;

      case (responseError?.body?.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        break;

      case (responseError?.body?.code === -4003):
        // "You do not have permission to perform this operation", -4003
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.General.Forbidden.description.pt_br,
        });
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      default:
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro ao carregar as categorias. Por favor, tente novamente mais tarde.',
        });
        break;
    }
  };

  useEffect(() => {
    setLoading(false);

    if ((userPermissions || []).includes('Visualizar notificações')) {
      getDataCategories(); // carrega lista de categorias para o dropdown
      fetchData(1); // carrega lista de notificaçõe para a tabela
      runOnMount();
    } else {
      notification.error({
        message: 'Erro',
        description: 'Desculpe, o usuário não possui permissão para realizar a consulta.',
      });
    }

    return () => {
      setLoading(false);
    };
  }, []);

  // constante utilizada para ancorar os índices do carregamento de dados da lista de notificações
  const columns = [
    {
      title: <div className="table-titles">ID</div>,
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: <div className="table-titles">Data de criação</div>,
      key: 'creationDate',
      dataIndex: 'creationDate',

      render: (date: string): React.ReactNode => {
        return date
          ? moment(date, 'YYYY-MM-DDTHH:mm:ss.SSS[Z]').format('DD/MM/YYYY')
          : '-';
      },
    },
    {
      title: <div className="table-titles">Categoria</div>,
      key: 'notificationCategory',
      dataIndex: 'notificationCategory',

      render: (category: CategoryFromListAll): React.ReactNode => (category ? category.name : null),
    },
    {
      title: <div className="table-titles">Título</div>,
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: <div className="table-titles">Data de notificação</div>,
      dataIndex: 'desiredDateNotification',
      key: 'desiredDateNotification',
      render: (date: string) => (date ? moment(date, 'YYYY-MM-DDTHH:mm:ss.SSS[Z]').format('DD/MM/YYYY') : '-'),
    },
    {
      title: <div className="table-titles">Ações</div>,
      dataIndex: 'id',
      key: 'opcoes',

      render: (
        id: string | number,
        resObject: NotificationFromListAll): React.ReactNode => {
        return (
          <>
            {
              !resObject.messageSend && (userPermissions || []).includes('Editar notificações')
                ? (
                  <>
                    <Button
                      type="link"
                      title="Editar notificação"
                      icon={<EditOutlined />}
                      href={`/system/notifications/${resObject.id}/edit`}
                      onClick={
                        (eventt): void => {
                          eventt.preventDefault();
                          navigate(`/system/notifications/${resObject.id}/edit`);
                        }
                      }
                    />
                    <Divider type="vertical" />
                  </>
                )
                : null
            }

            {
              (userPermissions || []).includes('Editar notificações')
                ? (
                  <Button
                    type="link"
                    title="Remover notificação"
                    icon={<DeleteOutlined />}
                    onClick={
                      (): void => {
                        Modal.confirm({
                          title: 'Atenção',
                          content: 'Deseja realmente excluir esta notificação?',
                          okText: 'Sim, excluir',
                          cancelText: 'Não, cancelar',
                          autoFocusButton: null,
                          icon: (<WarningOutlined />),
                          onOk: () => {
                            handleDeleteNotification(id as unknown as number);
                          },
                        });
                      }
                    }
                  />
                )
                : null
            }
          </>
        );
      },
    },
  ];

  return (
    <div className="Notifications">
      <Spin
        tip="Carregando..."
        spinning={loading}
      >
        <Form
          scrollToFirstError
          layout="vertical"
          form={formFilterInstance}
          onFinish={handleFilter}
        >
          <Row gutter={24} className="title-container">
            <Col
              xs={{ span: 24 }}
              md={{ span: 24 }}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <div>
                <span className="page-title">Notificações</span>
              </div>

              {
                (userPermissions || []).includes('Solicitar notificações')
                  ? (
                    <div className="filters-container">
                      <Button
                        block
                        className="btn-filter"
                        type="primary"
                        href="/system/notifications/new"
                        onClick={
                          (eventt): void => {
                            eventt.preventDefault();
                            navigate('/system/notifications/new');
                          }
                        }
                      >
                        <PlusIcon />
                        Cadastrar notificação
                      </Button>
                    </div>
                  )
                  : null
              }
            </Col>
          </Row>

          <Row
            gutter={24}
            className="filters-container"
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-start',
            }}
          >
            <Col xs={{ span: 24 }} lg={{ span: 6 }}>
              <Form.Item
                label="Categoria"
                name="categoryFilter"
              >
                <Select
                  showSearch
                  optionFilterProp="children"
                  placeholder="Escolha uma categoria"
                >
                  {
                    listCategories.map((v) => (
                      <Select.Option key={v.id} value={v.id}>
                        {v.name}
                      </Select.Option>
                    ))
                  }
                </Select>
              </Form.Item>
            </Col>

            <Col xs={{ span: 24 }} lg={{ span: 6 }}>
              <Form.Item
                label="Título"
                name="titleFilter"
                rules={[]}
              >
                <Input
                  style={{ width: '100%' }}
                  placeholder="Digite um título"
                >
                </Input>
              </Form.Item>
            </Col>

            <Col xs={{ span: 24 }} lg={{ span: 6 }}>
              <Form.Item
                label="Período"
                name="intervalFilter"
                className="ListNotifications__IntervalFilterFormItem"
                rules={[{ validator: validateDateRange }]}
                initialValue={[moment().subtract(30, 'day'), moment()]}
              >
                <DatePicker.RangePicker
                  className="ListNotifications__IntervalFilterDatepicker"
                  style={{ width: '100%' }}
                  placeholder={['Inicio', 'Fim']}
                  format="DD/MM/YYYY"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row
            gutter={24}
            className="filters-container"
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-end',
            }}
          >
            <Col xs={{ span: 24 }} lg={{ span: 6 }}>
              <Form.Item
                label=" "
              >
                <Button
                  className="btn-clean"
                  htmlType="reset"
                  onClick={
                    (): void => {
                      cleanFilterFields();
                    }
                  }
                >
                  <FilterRemoveIcon />
                  Limpar
                </Button>
              </Form.Item>
            </Col>

            <Col xs={{ span: 24 }} lg={{ span: 6 }}>
              <Form.Item
                label=" "
              >
                <Button
                  className="btn-filter"
                  type="primary"
                  htmlType="submit"
                >
                  <FilterIcon />
                  Filtrar
                </Button>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24} className="list-container">
            <Col span={24}>
              <Row gutter={24}>
                <Col span={24}>
                  <Table
                    rowKey={(record: NotificationFromListAll): number => record.id}
                    columns={columns}
                    dataSource={data}
                    pagination={false}
                  />
                </Col>
              </Row>
              <Row gutter={24}>
                <Col span={24}>
                  <CustomPagination
                    paginationInfo={paginationInfo}
                    fetchFunc={fetchData}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        </Form>
      </Spin>
    </div>
  );
};

Notifications.propTypes = {};
Notifications.defaultProps = {};

export default Notifications;
